import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Upload, Loader2, CheckCircle, Video, Plus, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { scoreBucket, toneClass } from '../utils/coachLanguage';
import api from '../services/api';
import { toast } from 'sonner';
import { useVideoMovementAnalyzer } from '../hooks/useVideoMovementAnalyzer';
import { analyzeMovement } from '../lib/analyzers';
import { buildSessionsAnalysis } from '../lib/videoSessionsAnalyzer';

const viewWord = (v) => (v === 'front' ? 'front' : v === 'side' ? 'side' : v || 'side');
const VIEW_UNLOCKS = { front: 'knee tracking & left/right symmetry', side: 'depth & trunk angle' };

// Average of the distinct captured-view scores (matches the backend's
// per-exercise form_score roll-up). `checks` is an array of per-clip results.
export const combinedFormScore = (checks) => {
  const byView = {};
  (checks || []).forEach((c) => {
    if (typeof c?.score === 'number') byView[c.view_detected || 'unknown'] = c.score;
  });
  const vals = Object.values(byView);
  return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
};

// Per-exercise "Check your form" control.
//
// IMPORTANT: this now runs the EXACT same analysis as the live camera / the
// /analyze page — MediaPipe in the BROWSER over the picked clip → the unified
// `analyzeMovement` + `buildSessionsAnalysis` engine → /interpret for the
// coach narrative → saved onto the session. The raw video never leaves the
// device (only the small joint-angle JSON is sent). During the workout we show
// just the score; the full rich report renders on the completed session screen.
//   variant 'full'    → rich card under the live camera (the single uploader)
//   variant 'compact' → tiny read-only status on each exercise-list row
export const FormCheckControl = ({
  exerciseName,
  exerciseIndex,
  sessionId,
  result,        // array of per-clip results | undefined
  onResult,      // (exerciseIndex, clipResult) => void
  variant = 'full',
}) => {
  const [guidance, setGuidance] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const pendingViewRef = useRef(null);   // which angle we asked the user to film
  const { progress, analyze, videoRef, canvasRef } = useVideoMovementAnalyzer();

  const checks = Array.isArray(result) ? result : (result ? [result] : []);
  const combined = combinedFormScore(checks);

  // Recommended camera angle(s) — fetched for the rich card only.
  useEffect(() => {
    if (variant !== 'full' || !exerciseName) return;
    let cancelled = false;
    api.getExerciseGuidance(exerciseName)
      .then((res) => { if (!cancelled) setGuidance(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [variant, exerciseName]);

  const pickFile = (view = null) => {
    pendingViewRef.current = view;
    fileRef.current?.click();
  };

  const handleFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^video\//.test(file.type)) { toast.error('Please pick an mp4, mov or webm video.'); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error('Keep clips under 100 MB.'); return; }

    const requestedView = pendingViewRef.current || guidance?.primary_view || null;
    setProcessing(true);
    setError(null);
    let url;
    try {
      url = URL.createObjectURL(file);
      const v = videoRef.current;
      v.src = url;
      // analyze() waits for metadata + handles the phone .mov Infinity-duration
      // bug, then seeks the clip frame-by-frame through MediaPipe in-browser.
      const frames = await analyze(v, { fps: 10 });
      if (!frames || frames.length === 0) throw new Error('no-frames');

      const movementId = guidance?.movement_id || 'other';
      const metrics = analyzeMovement(movementId, frames);

      // Same exercise definition the live Sessions use → identical joint-angle rows.
      let definition = null;
      try { definition = (await api.getExerciseDefinition(exerciseName)).data || null; } catch (_) { /* non-fatal */ }
      const sa = definition
        ? buildSessionsAnalysis({ frames, definition, metrics, exerciseName })
        : null;

      const view = metrics.view_detected || requestedView || 'side';
      const isKnown = movementId && movementId !== 'auto' && movementId !== 'other';
      const interp = await api.interpretMovement({
        movement_id: isKnown ? movementId : 'other',
        custom_movement_name: isKnown ? null : exerciseName,
        metrics,
        view_detected: view,
        sessions_analysis: sa,
      });
      const saWithNarr = sa ? { ...sa, narrative: interp.data?.narrative || null } : null;
      const score = sa?.score != null
        ? Math.round(sa.score)
        : (interp.data?.report?.overall_score ?? null);

      await api.saveFormCheck({
        session_id: sessionId,
        exercise_index: exerciseIndex,
        exercise_name: exerciseName,
        movement_id: movementId,
        view_detected: view,
        score,
        sessions_analysis: saWithNarr || { narrative: interp.data?.narrative || null },
      });

      onResult?.(exerciseIndex, {
        score,
        view_detected: view,
        movement_label: interp.data?.movement_label || exerciseName,
        sessions_analysis: saWithNarr,
        rep_count: metrics.rep_count,
      });
      toast.success(`Form scored — ${score ?? '—'}/100`);
    } catch (err) {
      const msg = err?.message === 'no-frames'
        ? "Couldn't see your body in that clip — film your whole body, well lit, from the recommended angle."
        : 'Could not analyze that clip. Try a clearer one.';
      setError(msg);
      toast.error(msg);
    } finally {
      setProcessing(false);
      if (url) URL.revokeObjectURL(url);
    }
  }, [guidance, exerciseName, sessionId, exerciseIndex, onResult, analyze, videoRef]);

  const pct = Math.round((progress || 0) * 100);

  const hiddenMedia = (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={handleFile}
        data-testid={`form-check-input-${exerciseIndex}`}
      />
      <video ref={videoRef} className="hidden" playsInline muted preload="metadata" />
      <canvas ref={canvasRef} className="hidden" />
    </>
  );

  // ── compact (exercise-list row) — READ-ONLY status only ─────────────────
  // The single upload action lives in the `full` card under the camera. This
  // row just MIRRORS that exercise's saved Form score (or "not checked").
  if (variant === 'compact') {
    const bk = scoreBucket(combined);
    return (
      <div className="mt-1.5 pl-9" data-testid={`form-check-compact-${exerciseIndex}`}>
        <span
          className="inline-flex items-center gap-1.5 text-xs"
          data-testid={combined != null ? `form-check-score-${exerciseIndex}` : `form-check-status-${exerciseIndex}`}
        >
          {combined != null ? (
            <>
              <Camera className="w-3 h-3 text-zinc-500" />
              <span className={toneClass(bk.tone)}>Form {combined}/100</span>
              <span className="text-zinc-600">· {checks.length} clip{checks.length === 1 ? '' : 's'}</span>
            </>
          ) : (
            <span className="text-zinc-600 inline-flex items-center gap-1.5">
              <Camera className="w-3 h-3" /> Form not checked
            </span>
          )}
        </span>
      </div>
    );
  }

  // ── full (the single uploader, under the camera) ────────────────────────
  const recommended = guidance?.recommended_views || [];
  const capturedViews = new Set(checks.map((c) => c.view_detected).filter(Boolean));
  const missingViews = recommended.filter((v) => !capturedViews.has(v));

  return (
    <div className="mt-6 border border-zinc-800 rounded-sm p-4 bg-zinc-900/40" data-testid={`form-check-panel-${exerciseIndex}`}>
      {hiddenMedia}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Video className="w-4 h-4 text-accent-purple flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-zinc-200">Check your form <span className="text-zinc-500 font-normal">(optional)</span></p>
            <p className="text-xs text-zinc-500">Upload a short clip — analyzed on your device, same report as the live camera. The video stays private.</p>
          </div>
        </div>
        {checks.length === 0 && (
          <Button
            onClick={() => pickFile(null)}
            disabled={processing}
            className="hpme-btn-secondary whitespace-nowrap"
            data-testid={`form-check-upload-btn-${exerciseIndex}`}
          >
            {processing
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {pct ? `Analyzing ${pct}%` : 'Analyzing…'}</>
              : <><Upload className="w-4 h-4 mr-2" /> Upload clip</>}
          </Button>
        )}
      </div>

      {/* Guidance — shown before the first clip */}
      {guidance && checks.length === 0 && (
        <p className="text-xs text-zinc-400 mt-3 flex items-start gap-1.5" data-testid={`form-check-guidance-${exerciseIndex}`}>
          <Camera className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
          <span>
            Film from the <span className="text-primary font-semibold uppercase">{guidance.primary_view}</span>
            {guidance.dual && (
              <span className="text-zinc-500"> — this move is best checked from {guidance.recommended_views.join(' + ')}</span>
            )}
            {' — '}{guidance.view_instructions?.[guidance.primary_view]}
          </span>
        </p>
      )}

      {processing && (
        <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1.5" data-testid={`form-check-progress-${exerciseIndex}`}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading your movement on-device{pct ? ` — ${pct}%` : ''}…
        </p>
      )}

      {error && <p className="text-xs text-red-400 mt-2" data-testid={`form-check-error-${exerciseIndex}`}>{error}</p>}

      {/* Score-only during the workout — full breakdown lives in the session report */}
      {checks.length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-800" data-testid={`form-check-result-${exerciseIndex}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span className="text-sm text-zinc-200">Form scored</span>
            {checks.map((c, i) => {
              const bk = scoreBucket(c.score);
              return (
                <span key={`${c.view_detected || 'v'}-${i}`} className="inline-flex items-center gap-1.5" data-testid={`form-check-view-${exerciseIndex}-${c.view_detected || 'unknown'}`}>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    {viewWord(c.view_detected)}
                  </span>
                  <span className={`font-secondary font-bold text-sm ${toneClass(bk.tone)}`}>
                    {c.score != null ? Math.round(c.score) : '—'}/100
                  </span>
                </span>
              );
            })}
            <button
              type="button"
              onClick={() => pickFile(null)}
              disabled={processing}
              className="ml-auto text-xs text-zinc-400 hover:text-white transition-colors disabled:opacity-60"
              data-testid={`form-check-recheck-${exerciseIndex}`}
            >
              {processing ? 'Analyzing…' : 'Re-check'}
            </button>
          </div>

          <p className="text-xs text-zinc-500 mt-2.5 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-accent-purple flex-shrink-0" />
            Full joint-by-joint breakdown &amp; coaching cues land in your session report when you finish.
          </p>

          {/* Optional second-angle opt-in (dual movements) */}
          {missingViews.length > 0 && (
            <div className="mt-3 flex items-center justify-between gap-3 bg-primary/5 border border-primary/20 rounded-sm px-3 py-2" data-testid={`form-check-add-angle-${exerciseIndex}`}>
              <p className="text-xs text-zinc-400">
                Add a <span className="text-primary font-semibold uppercase">{missingViews[0]}</span> clip to also check {VIEW_UNLOCKS[missingViews[0]] || 'more angles'}.
              </p>
              <Button
                onClick={() => pickFile(missingViews[0])}
                disabled={processing}
                className="hpme-btn-secondary whitespace-nowrap text-xs px-3 py-1.5 h-auto"
                data-testid={`form-check-add-angle-btn-${exerciseIndex}`}
              >
                {processing
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {pct ? `${pct}%` : '…'}</>
                  : <><Plus className="w-3.5 h-3.5 mr-1.5" /> Add {missingViews[0]} view</>}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FormCheckControl;
